import React, { useState } from 'react'
import {
    Badge,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    Paper,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import VisibilityIcon from '@material-ui/icons/Visibility'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'
import EventIcon from '@material-ui/icons/Event'
import RoomIcon from '@material-ui/icons/Room'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty'
import AssignmentIcon from '@material-ui/icons/Assignment'
import AddIcon from '@material-ui/icons/Add'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined'
import { Container } from '../../components'
import { vendorProjectApi } from '../../contexts'
import {
    PRIORITY_COLORS,
    PRIORITY_LABELS,
    VENDOR_PROJECT_BUCKET_COLORS,
    VendorProject,
    VendorTask,
    getDaysPastDue,
    getProjectBucket,
    safeDateOnly,
} from '../../models'
import { useUser } from '../../hooks'
import { typographyStyle } from '../../theme/typographyStyle'
import { TaskCard } from './TaskCard'
import { VendorTaskForm } from './VendorTaskForm'

interface Props {
    project: VendorProject
    onEdit: () => void
    onView: () => void
    onOpenMessages?: (project: VendorProject) => void
    onOpenTaskMessages?: (task: VendorTask) => void
}

const formatRange = (project: VendorProject): string => {
    const s = safeDateOnly(project.start_date)
    const e = safeDateOnly(project.end_date)
    if (!s && !e) return 'No dates set'
    if (s && e) return `${s} → ${e}`
    return s ?? e ?? 'No dates set'
}

interface PillProps {
    icon?: React.ReactNode
    bg: string
    fg: string
    children: React.ReactNode
}

const Pill = ({ icon, bg, fg, children }: PillProps) => {
    const theme = useTheme()
    return (
        <Container
            alignItems="center"
            style={{
                backgroundColor: bg,
                color: fg,
                padding: theme.spacing(0.25, 1),
                borderRadius: 999,
                fontWeight: 700,
                ...typographyStyle(theme.typography.caption),
                gap: 4,
            }}
        >
            {icon}
            {children}
        </Container>
    )
}

export const VendorProjectCard = (props: Props) => {
    const {
        project,
        onEdit,
        onView,
        onOpenMessages,
        onOpenTaskMessages,
    } = props
    const theme = useTheme()
    const { workspaceUser } = useUser()
    const { deleteVendorProject } = vendorProjectApi()

    const [expanded, setExpanded] = useState(false)
    const [hovered, setHovered] = useState(false)
    const [createTaskOpen, setCreateTaskOpen] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)

    const taskCount = project.taskCount ?? project.tasks?.length ?? 0
    const completed = project.completedTaskCount ?? 0
    const inProgress = project.inProgressTaskCount ?? 0
    const daysPastDue = getDaysPastDue(project)
    const overdue = daysPastDue > 0
    const showAssignedBy =
        !!project.assigned_by && project.assigned_by.id !== workspaceUser?.id
    const completionPct =
        taskCount > 0 ? Math.round((completed / taskCount) * 100) : 0

    const bucket = getProjectBucket(project)
    const bucketColors = VENDOR_PROJECT_BUCKET_COLORS[bucket]
    const priorityColors = PRIORITY_COLORS[project.priority]
    const sourceDocumentUrl = project.source_document?.document

    return (
        <Paper
            elevation={hovered ? 4 : 0}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                marginBottom: theme.spacing(2),
                borderRadius: 12,
                border: `1px solid ${theme.palette.divider}`,
                borderLeft: `4px solid ${bucketColors.fg}`,
                overflow: 'hidden',
                transition: 'box-shadow 150ms ease, transform 150ms ease',
                transform: hovered ? 'translateY(-1px)' : 'none',
                flexShrink: 0,
            }}
        >
            <Container
                alignItems="flex-start"
                style={{ padding: theme.spacing(2) }}
            >
                <IconButton
                    size="small"
                    onClick={() => setExpanded((v) => !v)}
                    style={{
                        marginRight: theme.spacing(1),
                        marginTop: 2,
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 200ms ease',
                    }}
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                >
                    <ExpandMoreIcon />
                </IconButton>

                <Container direction="column" flex={1} style={{ minWidth: 0 }}>
                    <Container
                        alignItems="center"
                        style={{ gap: theme.spacing(1), flexWrap: 'wrap' }}
                    >
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.subtitle1),
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {project.title}
                        </Container>
                        <Pill bg={bucketColors.bg} fg={bucketColors.text}>
                            {bucketColors.label}
                            {bucket === 'overdue' && overdue
                                ? ` · ${daysPastDue}d`
                                : ''}
                        </Pill>
                        <Pill bg={priorityColors.bg} fg={priorityColors.text}>
                            {PRIORITY_LABELS[project.priority]}
                        </Pill>
                    </Container>

                    <Container
                        alignItems="center"
                        style={{
                            marginTop: theme.spacing(1),
                            color: theme.palette.text.secondary,
                            ...typographyStyle(theme.typography.body2),
                            flexWrap: 'wrap',
                            gap: theme.spacing(1.5),
                        }}
                    >
                        <Container alignItems="center" style={{ gap: 4 }}>
                            <EventIcon
                                fontSize="small"
                                style={{ opacity: 0.7 }}
                            />
                            {formatRange(project)}
                        </Container>
                        {project.address && (
                            <Container alignItems="center" style={{ gap: 4 }}>
                                <RoomIcon
                                    fontSize="small"
                                    style={{ opacity: 0.7 }}
                                />
                                {project.address}
                            </Container>
                        )}
                        {showAssignedBy && (
                            <Container alignItems="center" style={{ gap: 4 }}>
                                <PersonOutlineIcon
                                    fontSize="small"
                                    style={{ opacity: 0.7 }}
                                />
                                Assigned by{' '}
                                {project.assigned_by.name_field ?? 'Unknown'}
                            </Container>
                        )}
                    </Container>

                    <Container
                        alignItems="center"
                        style={{
                            marginTop: theme.spacing(1),
                            flexWrap: 'wrap',
                            gap: theme.spacing(0.75),
                        }}
                    >
                        <Pill
                            bg="#f5f5f5"
                            fg={theme.palette.text.primary}
                            icon={<AssignmentIcon style={{ fontSize: 14 }} />}
                        >
                            {taskCount} task{taskCount === 1 ? '' : 's'}
                        </Pill>
                        <Pill
                            bg="#e8f5e9"
                            fg="#1b5e20"
                            icon={<CheckCircleIcon style={{ fontSize: 14 }} />}
                        >
                            {completed} done
                        </Pill>
                        <Pill
                            bg="#e3f2fd"
                            fg="#0d47a1"
                            icon={
                                <HourglassEmptyIcon style={{ fontSize: 14 }} />
                            }
                        >
                            {inProgress} in progress
                        </Pill>
                        {overdue && (
                            <Pill
                                bg="#ffebee"
                                fg="#b71c1c"
                                icon={
                                    <ErrorOutlineIcon
                                        style={{ fontSize: 14 }}
                                    />
                                }
                            >
                                {daysPastDue} day
                                {daysPastDue === 1 ? '' : 's'} past due
                            </Pill>
                        )}
                    </Container>

                    {taskCount > 0 && (
                        <Container
                            direction="column"
                            style={{ marginTop: theme.spacing(1.25) }}
                        >
                            <Container
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    color: theme.palette.text.secondary,
                                    marginBottom: 4,
                                    fontWeight: 600,
                                }}
                            >
                                {completionPct}% complete
                            </Container>
                            <LinearProgress
                                variant="determinate"
                                value={completionPct}
                                style={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: '#eceff1',
                                }}
                            />
                        </Container>
                    )}
                </Container>

                <Container
                    alignItems="center"
                    style={{ marginLeft: theme.spacing(1) }}
                >
                    {sourceDocumentUrl && (
                        <Tooltip
                            title={
                                project.source_document?.name
                                    ? `Open ${project.source_document.name}`
                                    : 'Open source document'
                            }
                        >
                            <IconButton
                                size="small"
                                onClick={() =>
                                    window.open(
                                        sourceDocumentUrl,
                                        '_blank',
                                        'noopener,noreferrer',
                                    )
                                }
                            >
                                <DescriptionOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip
                        title={
                            project.channel
                                ? 'Open project messages'
                                : 'No chat channel for this project yet'
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                disabled={!project.channel || !onOpenMessages}
                                onClick={() => {
                                    if (project.channel && onOpenMessages) {
                                        onOpenMessages(project)
                                    }
                                }}
                            >
                                <Badge
                                    badgeContent={project.message_count ?? 0}
                                    color="secondary"
                                    overlap="rectangle"
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    invisible={
                                        !project.message_count ||
                                        project.message_count <= 0
                                    }
                                    style={{ marginRight: 4, marginTop: 2 }}
                                >
                                    <ChatBubbleOutlineIcon fontSize="small" />
                                </Badge>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="View details">
                        <IconButton size="small" onClick={onView}>
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit project">
                        <IconButton size="small" onClick={onEdit}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete project">
                        <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(true)}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Container>
            </Container>

            <Collapse in={expanded} timeout={250} unmountOnExit>
                <Divider />
                <Container
                    direction="column"
                    style={{
                        padding: theme.spacing(2),
                        backgroundColor: '#fafbfb',
                    }}
                >
                    {project.description && (
                        <Container
                            style={{
                                marginBottom: theme.spacing(2),
                                color: theme.palette.text.secondary,
                                ...typographyStyle(theme.typography.body2),
                                whiteSpace: 'pre-wrap',
                                fontStyle: 'italic',
                                paddingLeft: theme.spacing(1.5),
                                borderLeft: `3px solid ${theme.palette.divider}`,
                            }}
                        >
                            {project.description}
                        </Container>
                    )}

                    <Container alignItems="center">
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.subtitle2),
                                fontWeight: 700,
                            }}
                        >
                            Tasks
                            <span
                                style={{
                                    marginLeft: 6,
                                    color: theme.palette.text.secondary,
                                    fontWeight: 400,
                                }}
                            >
                                ({taskCount})
                            </span>
                        </Container>
                        <Container flex={1} />
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateTaskOpen(true)}
                        >
                            Add Task
                        </Button>
                    </Container>

                    {(project.tasks ?? []).length === 0 ? (
                        <Container
                            style={{
                                marginTop: theme.spacing(2),
                                padding: theme.spacing(2),
                                color: theme.palette.text.secondary,
                                fontStyle: 'italic',
                                textAlign: 'center',
                                border: `1px dashed ${theme.palette.divider}`,
                                borderRadius: 8,
                                backgroundColor: 'white',
                            }}
                        >
                            No tasks yet. Click &quot;Add Task&quot; to create
                            one.
                        </Container>
                    ) : (
                        <Container direction="column">
                            {(project.tasks ?? []).map((task) => (
                                <TaskCard
                                    key={task.id}
                                    task={task}
                                    project={project}
                                    onOpenMessages={onOpenTaskMessages}
                                />
                            ))}
                        </Container>
                    )}
                </Container>
            </Collapse>

            <VendorTaskForm
                open={createTaskOpen}
                project={project}
                onClose={() => setCreateTaskOpen(false)}
            />

            <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
            >
                <DialogTitle>Delete Project</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete &quot;{project.title}
                        &quot;? This will also delete all of its tasks. This
                        action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={() => setConfirmDelete(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={async () => {
                            await deleteVendorProject(project.id)
                            setConfirmDelete(false)
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
